/**
 * Currency Modal Component
 * Modal/Bottom Sheet for selecting currency
 */

import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../store/uiStore';
import {
  type SupportedCurrency,
  SUPPORTED_CURRENCIES,
  CURRENCY_SYMBOLS,
  CURRENCY_NAMES,
} from '../../types';

interface CurrencyModalProps {
  isOpen: boolean;
  selectedCurrency: SupportedCurrency;
  onSelect: (currency: SupportedCurrency) => void;
  onClose: () => void;
}

export function CurrencyModal({
  isOpen,
  selectedCurrency,
  onSelect,
  onClose,
}: CurrencyModalProps) {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  if (!isOpen) return null;

  const handleSelect = (currency: SupportedCurrency) => {
    onSelect(currency);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="currency-modal-backdrop"
      />

      {/* Modal / Bottom Sheet */}
      <div
        className={`relative w-full sm:max-w-sm mx-auto rounded-t-2xl sm:rounded-2xl shadow-xl ${
          isDark ? 'bg-slate-800' : 'bg-white'
        } animate-slide-up`}
        data-testid="currency-modal"
      >
        {/* Handle bar for mobile */}
        <div className="flex justify-center pt-3 sm:hidden">
          <div className={`w-12 h-1.5 rounded-full ${isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-slate-700/50">
          <h2 className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            {t('modals.currency.title', 'Selecionar Moeda')}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-full hover:bg-slate-700/50 transition-colors ${
              isDark ? 'text-slate-400' : 'text-slate-600'
            }`}
            data-testid="close-currency-modal-button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Currency List */}
        <div className="p-2">
          {SUPPORTED_CURRENCIES.map((currency) => (
            <button
              key={currency}
              onClick={() => handleSelect(currency)}
              data-testid={`currency-option-${currency}`}
              className={`w-full flex items-center gap-4 px-4 py-3 rounded-lg transition-colors ${
                selectedCurrency === currency
                  ? isDark
                    ? 'bg-primary-500/20 text-primary-400'
                    : 'bg-primary-50 text-primary-600'
                  : isDark
                    ? 'text-slate-200 hover:bg-slate-700/50'
                    : 'text-slate-700 hover:bg-slate-100'
              }`}
            >
              {/* Currency Symbol */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold ${
                  selectedCurrency === currency
                    ? 'bg-primary-500 text-white'
                    : isDark
                      ? 'bg-slate-700 text-slate-300'
                      : 'bg-slate-200 text-slate-600'
                }`}
              >
                {CURRENCY_SYMBOLS[currency]}
              </div>

              {/* Currency Details */}
              <div className="flex-1 text-left">
                <p className={`font-medium ${selectedCurrency === currency ? '' : isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                  {currency}
                </p>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {t(`currencies.${currency}`, CURRENCY_NAMES[currency])}
                </p>
              </div>

              {/* Selected Indicator */}
              {selectedCurrency === currency && (
                <svg
                  className="w-5 h-5 text-primary-500"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>

        {/* Info text */}
        <div className={`px-4 py-3 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <p className={`text-xs text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            {t('modals.currency.info', 'Valores serão convertidos para BRL automaticamente')}
          </p>
        </div>
      </div>
    </div>
  );
}
